// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import {Listbox, Popover, Transition} from '@headlessui/react';
import clsx from 'clsx';
import {FC, Fragment, useEffect, useState} from 'react';
import {usePopper} from 'react-popper';

import {ChevronDownIcon} from '~/components';
import {HelpIcon} from '../icons/HelpIcon';

const Sparkle = () => (
  <svg
    width="9"
    height="9"
    viewBox="0 0 9 9"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.85901 7.28142C4.98878 6.66202 5.29648 6.09593 5.74333 5.65458C6.19004 5.21326 6.75575 4.91683 7.36857 4.80284L9 4.5L7.36846 4.19716C6.75565 4.08315 6.19007 3.78674 5.74336 3.34542C5.29652 2.9041 4.98882 2.33799 4.85904 1.71858L4.50002 0L4.14099 1.71858C4.01122 2.33799 3.70352 2.90407 3.25667 3.34542C2.80996 3.78674 2.24425 4.08317 1.63143 4.19716L0 4.5L1.63154 4.80284C2.24435 4.91685 2.80993 5.21326 3.25664 5.65458C3.70348 6.0959 4.01118 6.66202 4.14096 7.28142L4.49998 9L4.85901 7.28142Z"
      fill="#F7F4F1"
    />
  </svg>
);

const InfoTooltip = () => (
  <>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11"
      height="13"
      viewBox="0 0 11 13"
      fill="none"
      className="absolute mt-[36px]"
    >
      <path
        d="M-2.84124e-07 6.5L10.2723 0.870834L10.2723 12.1292L-2.84124e-07 6.5Z"
        fill="#593B37"
      />
    </svg>
    <div
      className={clsx(
        'ml-[9px] rounded-lg border border-gray-200 bg-[#593B37] py-[8px] pl-[12px] pr-[8px] font-abcdiatype text-[11px] text-white shadow-lg',
        'flex min-h-[75px] min-w-[214px] max-w-[214px] flex-col',
      )}
    >
      <p>
        Our <span className="font-[GT-Alpina] italic">Promise</span>
      </p>
      {[
        'A reminder before your next charge',
        'Lifetime pricing',
        'Use anytime within 6 months',
      ].map((item) => (
        <div key={item} className="flex items-center">
          <div className="mr-[4px]">
            <Sparkle />
          </div>
          <p>{item}</p>
        </div>
      ))}
    </div>
  </>
);

type TProductFrequency = {
  frequency?: string;
  setFrequency: (_frequency: string | undefined) => void;
};

const OPTIONS = [
  {
    id: '1-month',
    name: 'Deliver every month',
    value: '1-month',
  },
  {
    id: '6-weeks',
    name: 'Deliver every 6 weeks',
    value: '6-weeks',
  },
  {
    id: '2-months',
    name: 'Deliver every 2 months',
    value: '2-months',
  },
];

export const ProductFrequency: FC<TProductFrequency> = ({
  frequency,
  setFrequency,
}) => {
  useEffect(() => {
    if (!frequency) {
      setFrequency(OPTIONS[0].value);
    }
  }, [frequency, setFrequency]);

  const [referenceElement, setReferenceElement] = useState();
  const [popperElement, setPopperElement] = useState();

  const {styles, attributes} = usePopper(referenceElement, popperElement, {
    placement: 'right',
  });

  const [showTooltip, setShowTooltip] = useState(false);

  const onMouseOver = () => {
    setShowTooltip(true);
  };

  const onMouseOut = () => {
    setShowTooltip(false);
  };

  const frequencyName = OPTIONS.find((item) => item.value === frequency);
  return (
    <Listbox
      value={frequencyName}
      onChange={(frequency) => setFrequency(frequency.value)}
    >
      {({open}) => (
        <>
          <div className="flex gap-2">
            <Listbox.Label className="block text-sm font-medium text-gray-700">
              Frequency
            </Listbox.Label>
            <Popover className="relative">
              <Popover.Button ref={(_ref: any) => setReferenceElement(_ref)}>
                <HelpIcon onMouseOver={onMouseOver} onMouseOut={onMouseOut} />
              </Popover.Button>

              {showTooltip && (
                <Popover.Panel
                  ref={(_ref: any) => setPopperElement(_ref)}
                  style={styles.popper}
                  className="z-20"
                  static
                  {...attributes.popper}
                >
                  <InfoTooltip />
                </Popover.Panel>
              )}
            </Popover>
          </div>

          <div className="relative">
            <Listbox.Button
              className={clsx(
                'flex w-full transform appearance-none justify-between bg-clay-100 bg-clip-padding bg-no-repeat px-8 py-3 text-sm text-gray-900 duration-300 focus:border-black focus:ring-black',
                open ? 'rounded-t-[1.5rem]' : 'rounded-[1.5rem]',
              )}
            >
              <span className="block max-w-[230px] truncate border">
                {frequencyName?.name}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-8">
                <ChevronDownIcon
                  fill="black"
                  className={clsx(
                    'transform duration-300',
                    open ? 'rotate-180' : '',
                  )}
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Listbox.Options className="absolute z-10 max-h-60 w-full overflow-auto rounded-b-[1.5rem] bg-clay-100 py-1 text-base shadow-md !outline-none sm:text-sm">
                {OPTIONS.map((plan) => (
                  <Listbox.Option
                    key={plan?.id}
                    className={({active}) =>
                      clsx(
                        active ? 'bg-acai-900 text-white' : 'text-gray-900',
                        'relative cursor-pointer select-none py-2',
                      )
                    }
                    value={plan}
                  >
                    {({selected}) => (
                      <>
                        <span
                          className={clsx(
                            selected ? 'font-semibold' : 'font-normal',
                            'block truncate px-8',
                          )}
                        >
                          {plan?.name}
                        </span>
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
};
